import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/material';
import useSnackBar from '../../../../hooks/useSnackBar/useSnackBar';
import useHttpError from '../../../../hooks/useHttpError/useHttpError';
import useModal from '../../../../hooks/useModal/useModal';
import {
  CancelScheduledOperationRequest,
  Device,
  GetDeviceByIdFn,
  Sim,
  SimUpdateRequest,
} from '../../../../models/UserDevicesModels';
import UsersService from '../../../../services/Users/UsersService';
import { setUserOperations } from '../../../../stores/ESimManagerStore';

function useUserDetailsTabs() {
  const { id } = useParams();
  const [devices, setDevices] = useState<Device[]>([]);
  const [sims, setSims] = useState<Sim[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPerformingAction, setIsPerformingAction] = useState(false);
  const snackBar = useSnackBar();
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const { getHttpReasonFromList } = useHttpError();
  const modal = useModal();

  /**
   * Fetches the devices and sims of the current user
   */
  const fetchUserDevices = () => {
    if (id) {
      setIsLoading(true);
      UsersService.api
        .fetchUserDevices(id, { status: 'ALL', deviceOnly: false })
        .then(
          (res) => {
            setDevices(res.data.devices);
            setSims(res.data.sims);
          },
          () => {
            snackBar.showSnackBar(t('pages.userDetails.fetchUserDevicesError'), 'error');
          },
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  /**
   * Fetches the current user operations
   */
  const fetchUserOperations = () => {
    if (id) {
      UsersService.api.fetchUserOperations({ id }).then(
        (res) => {
          dispatch(setUserOperations(res.data.userOperations));
        },
        () => {
          snackBar.showSnackBar(t('pages.userDetails.fetchUserOperationsError'), 'error');
        },
      );
    }
  };

  /**
   * Finds a device by id
   * @param deviceId
   */
  const getDeviceByDeviceId: GetDeviceByIdFn = (deviceId: string) => devices.find((d) => deviceId === d.id);

  React.useEffect(() => {
    fetchUserDevices();
    fetchUserOperations();
  }, [id]);

  /**
   * Resends a notification when a device has a status error
   * @param deviceId
   */
  const resendNotification = async (deviceId: string) => {
    setIsPerformingAction(true);
    UsersService.api.resendNotification({ simId: deviceId }).then(
      () => {
        fetchUserDevices();
        snackBar.showSnackBar(t('pages.userDetails.resendNotificationSuccess'), 'success');
        setIsPerformingAction(false);
      },
      () => {
        snackBar.showSnackBar(t('pages.userDetails.resendNotificationError'), 'error');
        setIsPerformingAction(false);
      },
    );
  };

  /**
   * Updated the sim on the remote server
   * @param req
   * @param errorCallback
   */
  const updateSimDetails = async (req: SimUpdateRequest, errorCallback: () => void) => {
    setIsLoading(true);
    UsersService.api.updateSim(req).then(
      () => {
        fetchUserDevices();
        snackBar.showSnackBar(t('pages.userDetails.updateSimSuccess'), 'success');
        setIsLoading(false);
      },
      (res) => {
        errorCallback();
        setIsLoading(false);
        const error = getHttpReasonFromList(
          res.response.data,
          [3041, 3052, 3060, 3070, 3071, 3103, 3139],
          t('pages.userDetails.updateSimError'),
        );
        snackBar.showSnackBar(error, 'error');
      },
    );
  };

  /**
   * Deletes the device
   * @param deviceId
   */
  const deleteDevice = async (deviceId: string) => {
    setIsLoading(true);
    UsersService.api.deleteDevice(deviceId).then(
      () => {
        fetchUserDevices();
        snackBar.showSnackBar(t('pages.deviceDetails.deleteDeviceSuccess'), 'success');
        setIsLoading(false);
      },
      () => {
        snackBar.showSnackBar(t('pages.deviceDetails.deleteDeviceError'), 'error');
        setIsLoading(false);
      },
    );
  };

  /**
   * Handler for when user presses the delete device
   * Checks if the schedulers contains the device
   * @param deviceId
   */
  const deleteDeviceHandler = async (deviceId: string) => {
    setIsLoading(true);
    UsersService.api.schedulerContainsDevice(deviceId).then(
      (res) => {
        if (res.data.hasSchedulers) {
          modal.showModal({
            modalTitle: t('pages.deviceDetails.warning'),
            modalDescription: (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <span>{t('pages.deviceDetails.scheduledWarningMessage1')}</span>
                <span>{t('pages.deviceDetails.scheduledWarningMessage2')}</span>
              </Box>
            ),
            confirmationModal: true,
            confirmationButtonLabel: t('common.delete'),
            onAccept: (setOpen) => {
              setOpen(false);
              deleteDevice(deviceId);
            },
            onDiscard: () => {
              setIsLoading(false);
            },
          });
        } else {
          deleteDevice(deviceId);
        }
      },
      () => {
        snackBar.showSnackBar(t('pages.deviceDetails.deleteDeviceError'), 'error');
        setIsLoading(false);
      },
    );
  };

  /**
   * Cancel sim scheduled operation
   * @param req
   */
  const cancelScheduledOperation = async (req: CancelScheduledOperationRequest) => {
    setIsLoading(true);
    UsersService.api.cancelScheduledOperation(req).then(
      () => {
        fetchUserDevices();
        snackBar.showSnackBar(t('pages.deviceDetails.cancelScheduledOperationSuccess'), 'success');
        setIsLoading(false);
      },
      () => {
        snackBar.showSnackBar(t('pages.deviceDetails.cancelScheduledOperationError'), 'error');
        setIsLoading(false);
      },
    );
  };

  /**
   * Handler to refresh user devices and operations
   */
  const onActionSuccess = () => {
    fetchUserDevices();
    fetchUserOperations();
  };

  return {
    devices,
    sims,
    isLoading,
    isPerformingAction,
    resendNotification,
    getDeviceByDeviceId,
    fetchUserDevices,
    updateSimDetails,
    deleteDeviceHandler,
    cancelScheduledOperation,
    onActionSuccess,
  };
}

export default useUserDetailsTabs;
